/**
 * The admin's half of an import: PDFs without a source and sources without a PDF side by
 * side, matched by selecting a PDF and then a row (or a suggestion). Automatic and manual
 * matches can be undone. Confirm starts conversion; it waits until every PDF is handled.
 */
import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Link2, Unlink } from 'lucide-react';
import { Button, Actions, StatusLine } from '../../../components/InstanceSettings/formPrimitives';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { matchPdf, unmatchRow, keepPdf, discardPdf, confirmImport } from './importApi';
import UnmatchedPdfCard, { PairCard, CardHead } from './UnmatchedPdfCard';
import { Lead, Meta, StatusChip, Table, TableScroll } from './styles';

const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
  margin: 16px 0;
`;

const ColumnTitle = styled.h3`
  margin: 0 0 10px;
  font-size: 14px;
  color: #fff;
`;

function matchLabel(match, t) {
  if (match?.own) return t('datasetImport.own');
  if (match?.auto) return `${t('datasetImport.auto')} · ${Math.round((match.score || 0) * 100)} % · ${t(`datasetImport.methods.${match.method}`)}`;
  return t('datasetImport.manual');
}

export default function MatchingView({ datasetId, imp, onChange, onCancel, onConfirmed }) {
  const { t } = useTranslation();
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const rowsById = useMemo(() => Object.fromEntries(imp.unmatchedRows.map((r) => [r.id, r])), [imp.unmatchedRows]);

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      const next = await fn();
      if (next?.id) onChange(next);
      return next;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const match = (pdfId, rowId) => run(async () => {
    const next = await matchPdf(datasetId, imp.id, pdfId, rowId);
    setSelectedPdf(null);
    return next;
  });

  const handleConfirm = async () => {
    const done = await run(() => confirmImport(datasetId, imp.id));
    if (done?.ok) onConfirmed();
  };

  const aiCalls = imp.stats?.ai?.calls || 0;
  const pdfsLeft = imp.unmatchedPdfs.length;

  return (
    <>
      <Meta>
        <span>{t('datasetImport.summary', { matched: imp.matched.length, pdfs: pdfsLeft, rows: imp.unmatchedRows.length })}</span>
        {aiCalls > 0 && <span>· {t('datasetImport.aiCalls', { count: aiCalls })}</span>}
        <span>· {imp.source_filename}{imp.archive_filename ? `, ${imp.archive_filename}` : ''}</span>
      </Meta>

      <Columns>
        <div>
          <ColumnTitle>{t('datasetImport.unmatchedPdfsTitle')} ({pdfsLeft})</ColumnTitle>
          {!pdfsLeft && <Lead>{t('datasetImport.noUnmatchedPdfs')}</Lead>}
          {imp.unmatchedPdfs.map((pdf) => (
            <UnmatchedPdfCard
              key={pdf.id}
              datasetId={datasetId}
              pdf={pdf}
              rowsById={rowsById}
              selected={selectedPdf === pdf.id}
              busy={busy}
              onSelect={setSelectedPdf}
              onMatch={match}
              onKeep={(pdfId, payload) => run(() => keepPdf(datasetId, imp.id, pdfId, payload))}
              onDiscard={(pdfId) => run(() => discardPdf(datasetId, imp.id, pdfId))}
            />
          ))}
        </div>

        <div>
          <ColumnTitle>{t('datasetImport.unmatchedRowsTitle')} ({imp.unmatchedRows.length})</ColumnTitle>
          {!imp.unmatchedRows.length && <Lead>{t('datasetImport.noUnmatchedRows')}</Lead>}
          {imp.unmatchedRows.map((row) => (
            <PairCard key={row.id}>
              <CardHead>{row.title}</CardHead>
              <Meta>
                {row.row_number && <span>{t('datasetImport.rowNumber', { n: row.row_number })}</span>}
                {row.publisher && <span>· {row.publisher}</span>}
              </Meta>
              <Meta>{row.link ? t('datasetImport.linkNote') : t('datasetImport.noLinkNote')}</Meta>
              {selectedPdf && (
                <Actions>
                  <Button $size="small" $variant="primary" disabled={busy} onClick={() => match(selectedPdf, row.id)}>
                    <Link2 /> {t('datasetImport.matchHere')}
                  </Button>
                </Actions>
              )}
            </PairCard>
          ))}
        </div>
      </Columns>

      {imp.matched.length > 0 && (
        <>
          <ColumnTitle>{t('datasetImport.matchedTitle')} ({imp.matched.length})</ColumnTitle>
          <TableScroll>
            <Table>
              <tbody>
                {imp.matched.map((row) => (
                  <tr key={row.id}>
                    <td>{row.title}{row.publisher ? <Meta>{row.publisher}</Meta> : null}</td>
                    <td>{row.filename}</td>
                    <td><StatusChip $status={row.match?.auto ? 'indexed' : 'pending_review'}>{matchLabel(row.match, t)}</StatusChip></td>
                    <td>
                      {!row.match?.own && (
                        <Button $size="small" $variant="ghost" disabled={busy}
                          onClick={() => run(() => unmatchRow(datasetId, imp.id, row.id))}>
                          <Unlink /> {t('datasetImport.unmatch')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        </>
      )}

      <Actions style={{ marginTop: 16 }}>
        <Button $variant="primary" disabled={busy || pdfsLeft > 0} onClick={handleConfirm}>
          {t('datasetImport.confirm')}
        </Button>
        <Button $variant="ghost" $tone="danger" disabled={busy} onClick={onCancel}>{t('datasetImport.cancel')}</Button>
        {pdfsLeft > 0 && <StatusLine $error>{t('datasetImport.confirmBlocked')}</StatusLine>}
        {error && <StatusLine $error>{error}</StatusLine>}
      </Actions>
    </>
  );
}
